import React, { useState, useEffect } from 'react';
import { CloseIcon, UploadCloudIcon, CautionIcon } from '../../../../public/icons';
import { useToast } from '../../../../context/ToastContext';
import adminCollectionService from '../../../../services/admin/admin-collectionService'; 
import axios from 'axios';

const AddCollectionModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  onSaveDraft,
  loading = false,
  selectedProduct = null 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    collectionType: 'parent',
    type: 'category',
    parentId: '',
    description: '',
    coverImage: null,
    countryCode: '',
    countryName: '',
    theme: '',
    
    // For ALL collection types - parent, sub, and standalone
    baseProductId: '',
    
    // For standalone collections
    standaloneProductDetails: {
      basePrice: '',
      stockQuantity: '',
      shortHighlight: '',
      specifications: {
        dimensions: '',
        weight: '',
        materials: [''],
        careInstructions: '',
        warranty: ''
      },
      images: [],
      tags: ['']
    },
    
    // For standalone collections: optional parent placement
    placementType: 'none', // 'none', 'parent', 'sub'
    selectedParentId: '',
    selectedSubParentId: '',
    selectedParentName: ''
  });
  
  const [imagePreview, setImagePreview] = useState(null);
  const [parentCollections, setParentCollections] = useState([]);
  const [allParentCollections, setAllParentCollections] = useState([]);
  const [subCollections, setSubCollections] = useState([]);
  const [baseProducts, setBaseProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState([]);

  const { error, success: showSuccess } = useToast();



  useEffect(() => {
  if (isOpen) {
    // Reset form when modal opens
    setFormData({
      name: '',
      collectionType: 'parent',
      type: 'category',
      parentId: '',
      description: '',
      coverImage: null,
      countryCode: '',
      countryName: '',
      theme: '',
      baseProductId: '',
      standaloneProductDetails: {
        basePrice: '',
        stockQuantity: '',
        shortHighlight: '',
        specifications: {
          dimensions: '',
          weight: '',
          materials: [''],
          careInstructions: '',
          warranty: ''
        },
        images: [],
        tags: ['']
      },
      placementType: 'none',
      selectedParentId: '',
      selectedSubParentId: '',
      selectedParentName: ''
    });
    setImagePreview(null);
    setAdditionalImages([]);
    setAdditionalImagePreviews([]);
    setParentCollections([]);
    setAllParentCollections([]);
    setSubCollections([]);
    
    // Fetch base products and parent collections
    fetchBaseProducts();
    fetchAllParentCollections();
  }
}, [isOpen]); // Remove selectedProduct from dependency array


  // Collection types for dropdown
  const collectionTypes = [
    { value: 'parent', label: 'Parent Collection', description: 'Top-level collection for a product' },
    { value: 'sub', label: 'Sub Collection', description: 'Child collection under a parent collection' },
    { value: 'standalone', label: 'Standalone Product', description: 'Independent product with full details' }
  ];

  // Placement types for standalone
  const placementTypes = [
    { value: 'none', label: 'No Parent (Top Level)' },
    { value: 'parent', label: 'Under Parent Collection' },
    { value: 'sub', label: 'Under Sub Collection' }
  ];

  // Fetch collection-based products
  const fetchBaseProducts = async () => {
    try {
      setLoadingProducts(true);
      console.log('🔄 [Modal] Fetching collection-based products...');
      
      const response = await adminCollectionService.getCollectionBasedProducts();
      
      if (response.success) {
        console.log('✅ [Modal] Collection-based products fetched:', response.data.products.length);
        console.log('📦 [Modal] Base products data:', JSON.stringify(response.data.products, null, 2));
        setBaseProducts(response.data.products);
      } else {
        error('Failed to fetch base products');
      }
    } catch (err) {
      console.error('💥 [Modal] Error fetching base products:', err);
      error('Failed to fetch base products');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Fetch all parent collections for standalone placement
  const fetchAllParentCollections = async () => {
    try {
      setLoadingCollections(true);
      console.log('🔄 [Modal] Fetching all parent collections...');
      
      const response = await adminCollectionService.getAllParentCollections({
        status: 'active'
      });
      
      if (response.success) {
        console.log('✅ [Modal] All parent collections fetched:', response.data.collections.length);
        setAllParentCollections(response.data.collections);
      } else {
        error('Failed to fetch parent collections');
      }
    } catch (err) {
      console.error('💥 [Modal] Error fetching parent collections:', err);
      error('Failed to fetch parent collections');
    } finally {
      setLoadingCollections(false);
    }
  };

  // Fetch parent collections for a specific product (for sub collections)
  const fetchParentCollections = async (productId) => {
    if (!productId) {
      setParentCollections([]);
      return;
    }

    // Validate productId format
    const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(productId);
    if (!isValidObjectId) {
      console.error('❌ [Modal] Invalid product ID format:', productId);
      setParentCollections([]);
      return;
    }

    try {
      setLoadingCollections(true);
      console.log('🔄 [Modal] Fetching parent collections for product:', productId);
      
      const response = await adminCollectionService.getParentCollectionsByProduct(productId);
      
      if (response.success) {
        console.log('✅ [Modal] Parent collections fetched:', response.data.collections.length);
        setParentCollections(response.data.collections);
      } else {
        error('Failed to fetch parent collections');
      }
    } catch (err) {
      console.error('💥 [Modal] Error fetching parent collections:', err);
      error('Failed to fetch parent collections');
    } finally {
      setLoadingCollections(false);
    }
  };

const fetchSubCollections = async (parentId) => {
  if (!parentId) {
    setSubCollections([]);
    return;
  }

  // Validate parentId format (this should be a collection ID, not product ID)
  const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(parentId);
  if (!isValidObjectId) {
    console.error('❌ [Modal] Invalid parent ID format:', parentId);
    setSubCollections([]);
    return;
  }

  try {
    setLoadingCollections(true);
    console.log('🔄 [Modal] Fetching sub collections for parent collection:', parentId);
    
    // Debug: Check what parent collections exist in allParentCollections
    const selectedParent = allParentCollections.find(c => (c.id || c._id) === parentId);
    console.log('📌 [Modal] Selected parent collection:', selectedParent);
    
    const response = await adminCollectionService.getSubCollectionsByParent(parentId, {
      status: 'active'
    });
    
    console.log('📦 [Modal] Sub collections API response:', response);
    
    if (response.success && response.data && response.data.collections) {
      console.log('✅ [Modal] Sub collections fetched:', response.data.collections.length);
      
      // ✅ FIX: Ensure each collection has an id field
      const formattedCollections = response.data.collections.map(col => ({
        ...col,
        id: col.id || col._id, // Ensure id field exists
        _id: col._id || col.id  // Ensure _id field exists
      }));
      
      setSubCollections(formattedCollections);
    } else {
      console.log('⚠️ [Modal] No sub collections found or invalid response format');
      setSubCollections([]);
    }
  } catch (err) {
    console.error('💥 [Modal] Error fetching sub collections:', err);
    error('Failed to fetch sub collections. Please try again.');
    setSubCollections([]);
  } finally {
    setLoadingCollections(false);
  }
};

  // Upload images to Cloudinary
  const uploadToCloudinary = async (file, isPrimary = false) => {
    const cloudinaryFormData = new FormData();
    cloudinaryFormData.append('file', file);
    cloudinaryFormData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
      cloudinaryFormData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      }
    );
    
    return {
      url: response.data.secure_url,
      publicId: response.data.public_id,
      width: response.data.width,
      height: response.data.height,
      format: response.data.format,
      isPrimary
    };
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Don't use selectedProduct directly - let user choose from dropdown
      setFormData({
        name: '',
        collectionType: 'parent',
        type: 'category',
        parentId: '',
        description: '',
        coverImage: null,
        countryCode: '',
        countryName: '',
        theme: '',
        baseProductId: '',
        standaloneProductDetails: {
          basePrice: '',
          stockQuantity: '',
          shortHighlight: '',
          specifications: {
            dimensions: '',
            weight: '',
            materials: [''],
            careInstructions: '',
            warranty: ''
          },
          images: [],
          tags: ['']
        },
        placementType: 'none',
        selectedParentId: '',
        selectedSubParentId: '',
        selectedParentName: ''
      });
      setImagePreview(null);
      setAdditionalImages([]);
      setAdditionalImagePreviews([]);
      setParentCollections([]);
      setAllParentCollections([]);
      setSubCollections([]);
      
      // Fetch base products and parent collections
      fetchBaseProducts();
      fetchAllParentCollections();
    }
  }, [isOpen]); // Remove selectedProduct from dependency array

  // Fetch parent collections when base product changes (for sub collections)
  useEffect(() => {
    if (formData.baseProductId && formData.collectionType === 'sub') {
      // Validate productId format before fetching
      const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(formData.baseProductId);
      if (isValidObjectId) {
        fetchParentCollections(formData.baseProductId);
      } else {
        console.error('❌ [Modal] Cannot fetch parent collections - invalid product ID:', formData.baseProductId);
        setParentCollections([]);
      }
    } else {
      setParentCollections([]);
    }
  }, [formData.baseProductId, formData.collectionType]);

  // Clean up image previews on unmount
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      additionalImagePreviews.forEach(preview => {
        URL.revokeObjectURL(preview);
      });
    };
  }, [imagePreview, additionalImagePreviews]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('standaloneProductDetails.')) {
      const path = name.replace('standaloneProductDetails.', '').split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current = newData.standaloneProductDetails;
        
        for (let i = 0; i < path.length - 1; i++) {
          current = current[path[i]];
        }
        
        if (type === 'checkbox') {
          current[path[path.length - 1]] = checked;
        } else {
          current[path[path.length - 1]] = value;
        }
        
        return newData;
      });
    } else if (name.startsWith('specifications.')) {
      const specField = name.replace('specifications.', '');
      setFormData(prev => ({
        ...prev,
        standaloneProductDetails: {
          ...prev.standaloneProductDetails,
          specifications: {
            ...prev.standaloneProductDetails.specifications,
            [specField]: value
          }
        }
      }));
    } else {
      // Handle base product selection
      if (name === 'baseProductId') {
        console.log('🔄 [Modal] Base product selected:', {
          value,
          type: typeof value,
          length: value?.length,
          isValidObjectId: /^[0-9a-fA-F]{24}$/.test(value)
        });
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
      // Handle placement type change
      else if (name === 'placementType') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          selectedParentId: '',
          selectedSubParentId: '',
          selectedParentName: ''
        }));
        
        if (value === 'parent') {
          setSubCollections([]);
        }
      } 
      // Handle parent selection change
     else if (name === 'selectedParentId') {
  const parentName = allParentCollections.find(c => c.id === value)?.name || '';
  setFormData(prev => ({
    ...prev,
    [name]: value,
    selectedSubParentId: '',
    selectedParentName: parentName
  }));
  
  // When in 'sub' placement type, fetch sub-collections of the selected parent collection
  if (value && formData.placementType === 'sub') {
    console.log('🔄 [Modal] Selected parent collection for sub placement, fetching sub-collections...');
    fetchSubCollections(value);
  }
}
      // Handle sub collection selection change
      else if (name === 'selectedSubParentId') {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
      // Handle collection type change
      else if (name === 'collectionType') {
        console.log('🔄 [Modal] Changing collection type to:', value);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          parentId: value !== 'sub' ? '' : prev.parentId,
          placementType: value === 'standalone' ? prev.placementType || 'none' : 'none'
        }));
      }
      // Handle category type change
      else if (name === 'type') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          countryCode: value !== 'country' ? '' : prev.countryCode,
          countryName: value !== 'country' ? '' : prev.countryName,
          theme: value !== 'theme' ? '' : prev.theme
        }));
      }
      else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };

  const handleCoverImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      if (file.size > 5 * 1024 * 1024) {
        error('Image size should be less than 5MB');
        return;
      }
      
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        error('Only JPG, PNG, and WebP images are allowed');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      
      setUploadingImage(true);
      try {
        const cloudinaryResponse = await uploadToCloudinary(file, true);
        
        setFormData(prev => ({
          ...prev,
          coverImage: {
            url: cloudinaryResponse.url,
            publicId: cloudinaryResponse.publicId,
            width: cloudinaryResponse.width,
            height: cloudinaryResponse.height,
            format: cloudinaryResponse.format
          }
        }));
        
        showSuccess('Cover image uploaded successfully');
      } catch (uploadError) {
        console.error('Image upload error:', uploadError);
        error('Failed to upload image. Please try again.');
        setImagePreview(null);
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleAdditionalImagesChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Validate total number of images (max 10 including cover)
    const totalImages = additionalImages.length + (formData.coverImage ? 1 : 0);
    if (totalImages + files.length > 10) {
      error('Maximum 10 images allowed (including cover)');
      return;
    }
    
    setUploadingImage(true);
    try {
      for (const file of files) {
        // Validate file
        if (file.size > 5 * 1024 * 1024) {
          error(`${file.name} is too large (max 5MB)`);
          continue;
        }
        
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
          error(`${file.name} is not a valid image type`);
          continue;
        }
        
        // Create preview
        const previewUrl = URL.createObjectURL(file);
        setAdditionalImagePreviews(prev => [...prev, previewUrl]);
        
        // Upload to Cloudinary
        const cloudinaryResponse = await uploadToCloudinary(file, false);
        
        // Add to additional images
        setAdditionalImages(prev => [...prev, cloudinaryResponse]);
      }
      
      showSuccess('Additional images uploaded successfully');
    } catch (uploadError) {
      console.error('Additional image upload error:', uploadError);
      error('Failed to upload some images. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });
    
    setAdditionalImagePreviews(prev => {
      const newPreviews = [...prev];
      URL.revokeObjectURL(newPreviews[index]);
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  const removeCoverImage = () => {
    setFormData(prev => ({ ...prev, coverImage: null }));
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (status = 'active') => {
    try {
      console.log('🔄 [Frontend] Preparing collection data...');
      
      // Validate form data
      let validationErrors = [];

      // Common validations
      if (!formData.name.trim()) {
        validationErrors.push('Collection name is required');
      }

      if (!formData.description.trim()) {
        validationErrors.push('Description is required');
      }

      // Base product is required for ALL collection types
      if (!formData.baseProductId) {
        validationErrors.push('Base product is required');
      } else {
        // Validate baseProductId is a valid MongoDB ObjectId
        const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(formData.baseProductId);
        if (!isValidObjectId) {
          validationErrors.push('Invalid base product ID format. Please select a valid product.');
        }
      }

      // Validations based on collection type
      if (formData.collectionType === 'sub') {
        if (!formData.parentId) {
          validationErrors.push('Parent collection is required for sub collections');
        } else {
          // Validate parentId is a valid MongoDB ObjectId
          const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(formData.parentId);
          if (!isValidObjectId) {
            validationErrors.push('Invalid parent collection ID format.');
          }
        }
      }

      // Validations for standalone collections
      if (formData.collectionType === 'standalone') {
        const { standaloneProductDetails } = formData;
        
        if (!standaloneProductDetails.basePrice) {
          validationErrors.push('Base price is required');
        } else if (isNaN(standaloneProductDetails.basePrice) || parseFloat(standaloneProductDetails.basePrice) < 0) {
          validationErrors.push('Please enter a valid base price');
        }
        
        if (!standaloneProductDetails.stockQuantity) {
          validationErrors.push('Stock quantity is required');
        } else if (isNaN(standaloneProductDetails.stockQuantity) || parseInt(standaloneProductDetails.stockQuantity) < 0) {
          validationErrors.push('Please enter a valid stock quantity');
        }
        
        if (!formData.coverImage) {
          validationErrors.push('Cover image is required for standalone products');
        }
        
      }

      // Additional validation based on type
      if (formData.type === 'country' && (!formData.countryCode || !formData.countryName)) {
        validationErrors.push('Country code and name are required for country collections');
      }

      if (formData.type === 'theme' && !formData.theme.trim()) {
        validationErrors.push('Theme is required for theme collections');
      }

      if (validationErrors.length > 0) {
        validationErrors.forEach(err => error(err));
        return;
      }

      // Format data for API
      const collectionData = {
        name: formData.name.trim(),
        collectionType: formData.collectionType,
        type: formData.type,
        description: formData.description.trim(),
        status: status,
        countryCode: formData.countryCode,
        countryName: formData.countryName,
        theme: formData.theme,
        // Add base product for ALL collection types
        productId: formData.baseProductId
      };

      // Add parent for sub collections
      if (formData.collectionType === 'sub') {
        collectionData.parentId = formData.parentId;
      }

      // Handle standalone collections
      if (formData.collectionType === 'standalone') {
        // Add product details - ONLY nested structure, NO root level fields
        collectionData.productDetails = {
          basePrice: parseFloat(formData.standaloneProductDetails.basePrice) || 0,
          stockQuantity: parseInt(formData.standaloneProductDetails.stockQuantity) || 0,
          shortHighlight: formData.standaloneProductDetails.shortHighlight?.trim() || '',
          specifications: {
            dimensions: formData.standaloneProductDetails.specifications.dimensions || '',
            weight: formData.standaloneProductDetails.specifications.weight || '',
            materials: formData.standaloneProductDetails.specifications.materials?.filter(m => m?.trim() !== '') || [],
            careInstructions: formData.standaloneProductDetails.specifications.careInstructions || '',
            warranty: formData.standaloneProductDetails.specifications.warranty || ''
          },
          tags: formData.standaloneProductDetails.tags?.filter(t => t?.trim() !== '') || []
        };
        
        // Combine all images (cover + additional)
        if (formData.coverImage) {
          const allImages = [formData.coverImage, ...additionalImages];
          collectionData.productDetails.images = allImages.map((img, index) => ({
            ...img,
            isPrimary: index === 0
          }));
        }
        
        // Handle optional parent placement
        if (formData.placementType !== 'none') {
          const parentId = formData.placementType === 'parent' 
            ? formData.selectedParentId 
            : formData.selectedSubParentId;
          
          const parentName = formData.placementType === 'parent'
            ? allParentCollections.find(c => c.id === formData.selectedParentId)?.name
            : subCollections.find(c => c.id === formData.selectedSubParentId)?.name;
          
          if (parentId) {
            // Validate parentId format
            if (/^[0-9a-fA-F]{24}$/.test(parentId)) {
              collectionData.parentCollection = {
                parentId: parentId,
                collectionType: formData.placementType,
                collectionName: parentName || ''
              };
              
              // Also set parentId for hierarchical structure
              collectionData.parentId = parentId;
            } else {
              console.error('❌ [Frontend] Invalid parent ID format:', parentId);
            }
          }
        }
        
        // ⚠️ IMPORTANT: Do NOT add basePrice or stockQuantity at root level
        // These fields should ONLY exist inside productDetails
      }

      // Add cover image for all collection types
      if (formData.coverImage) {
        collectionData.coverImage = formData.coverImage;
      }

      console.log('📤 [Frontend] Final collection data to send:', JSON.stringify(collectionData, null, 2));

      // Call appropriate handler
      if (status === 'active') {
        console.log('🚀 [Frontend] Calling onSubmit with status: active');
        await onSubmit(collectionData, formData.collectionType);
      } else {
        console.log('🚀 [Frontend] Calling onSaveDraft with status: draft');
        await onSaveDraft(collectionData, formData.collectionType);
      }

      // Close modal on success
      onClose();

    } catch (submitError) {
      console.error('💥 [Frontend] Error in handleSubmit:', submitError);
      error('Failed to submit collection data. Please check your inputs and try again.');
    }
  };

  const handleSaveDraft = () => {
    handleSubmit('draft');
  };

  const handleCreateActive = () => {
    handleSubmit('active');
  };

  // Get selected base product for display - with validation
  const selectedBaseProduct = baseProducts.find(p => {
    // Handle different ID field names
    const productId = p.id || p._id;
    return productId === formData.baseProductId;
  });
  
  // Get selected parent collection for display
  const selectedParentCollection = formData.placementType === 'parent' 
    ? allParentCollections.find(p => {
        const collectionId = p.id || p._id;
        return collectionId === formData.selectedParentId;
      })
    : null;

const selectedSubCollection = formData.placementType === 'sub'
  ? subCollections.find(s => {
      const collectionId = s.id || s._id;
      return collectionId === formData.selectedSubParentId;
    })
  : null;

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[70%] lg:w-[60%] text-left xl:w-[50%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b mx-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                {formData.collectionType === 'standalone' ? 'Add New Product' : 'Add New Collection'}
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                {formData.collectionType === 'standalone' 
                  ? 'Create a standalone product with full details'
                  : 'Create a collection to group related items'}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded disabled:opacity-50"
              disabled={loading}
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Collection Type Dropdown */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Type *
              </label>
              <select
                name="collectionType"
                value={formData.collectionType}
                onChange={handleChange}
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                disabled={loading}
              >
                {collectionTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              
              <p className="mt-2 text-sm text-gray-600">
                {collectionTypes.find(t => t.value === formData.collectionType)?.description}
              </p>
            </div>

            {/* Base Product Selection - SHOWS FOR ALL COLLECTION TYPES */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Base Product *
              </label>
              {loadingProducts ? (
                <div className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded flex items-center justify-center">
                  <span className="text-sm text-gray-500">Loading products...</span>
                </div>
              ) : (
                <select
                  name="baseProductId"
                  value={formData.baseProductId}
                  onChange={handleChange}
                  className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                  disabled={loading}
                  required
                >
                  <option value="">Select a base product</option>
                  {baseProducts.map(product => {
                    const productId = product.id || product._id;
                    return (
                      <option key={productId} value={productId}>
                        {product.name} ({product.baseProduct || 'Product'})
                      </option>
                    );
                  })}
                </select>
              )}
              {selectedBaseProduct && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-center gap-3">
                    {selectedBaseProduct.image && (
                      <img 
                        src={selectedBaseProduct.image} 
                        alt={selectedBaseProduct.name}
                        className="h-10 w-10 rounded object-cover"
                      />
                    )}
                    <div>
                      <p className="text-sm font-medium text-blue-900">{selectedBaseProduct.name}</p>
                      <p className="text-xs text-blue-700">
                        Base Product: {selectedBaseProduct.baseProduct || 'Not specified'}
                      </p>
                      <p className="text-xs text-blue-600">
                        Status: {selectedBaseProduct.status} | Stock: {selectedBaseProduct.stockQuantity}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Parent Collection Selection (for Sub Collections only) */}
            {formData.collectionType === 'sub' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                  Parent Collection *
                </label>
                {loadingCollections ? (
                  <div className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded flex items-center justify-center">
                    <span className="text-sm text-gray-500">Loading parent collections...</span>
                  </div>
                ) : (
                  <select
                    name="parentId"
                    value={formData.parentId}
                    onChange={handleChange}
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                    disabled={loading || !formData.baseProductId || !/^[0-9a-fA-F]{24}$/.test(formData.baseProductId)}
                    required
                  >
                    <option value="">Select a parent collection</option>
                    {parentCollections.map(collection => {
                      const collectionId = collection.id || collection._id;
                      return (
                        <option key={collectionId} value={collectionId}>
                          {collection.name}
                        </option>
                      );
                    })}
                  </select>
                )}
                {parentCollections.length === 0 && formData.baseProductId && /^[0-9a-fA-F]{24}$/.test(formData.baseProductId) && (
                  <p className="mt-2 text-sm text-amber-600">
                    No parent collections found for this product. Create a parent collection first.
                  </p>
                )}
              </div>
            )}

            {/* Parent Placement for Standalone Collections */}
            {formData.collectionType === 'standalone' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                  Parent Placement (Optional)
                </label>
                <div className="space-y-4">
                  <select
                    name="placementType"
                    value={formData.placementType}
                    onChange={handleChange}
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  >
                    {placementTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  
                  {formData.placementType === 'parent' && (
                    <div>
                      <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                        Select Parent Collection
                      </label>
                      <select
                        name="selectedParentId"
                        value={formData.selectedParentId}
                        onChange={handleChange}
                        className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                      >
                        <option value="">Select a parent collection</option>
                        {allParentCollections.map(collection => {
                          const collectionId = collection.id || collection._id;
                          return (
                            <option key={collectionId} value={collectionId}>
                              {collection.name}
                            </option>
                          );
                        })}
                      </select>
                      
                      {selectedParentCollection && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded">
                          <p className="text-sm font-medium text-green-900">
                            Will be placed under: {selectedParentCollection.name}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                 {formData.placementType === 'sub' && (
  <>
    <div>
      <label className="block text-sm font-medium text-[#1C2A46] mb-2">
        Select Parent Collection
      </label>
      <select
        name="selectedParentId"
        value={formData.selectedParentId}
        onChange={handleChange}
        className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
      >
        <option value="">Select a parent collection first</option>
        {allParentCollections.map(collection => {
          const collectionId = collection.id || collection._id;
          return (
            <option key={collectionId} value={collectionId}>
              {collection.name}
            </option>
          );
        })}
      </select>
    </div>
    
    {formData.selectedParentId && (
      <div>
        <label className="block text-sm font-medium text-[#1C2A46] mb-2">
          Select Sub Collection
        </label>
        <select
          name="selectedSubParentId"
          value={formData.selectedSubParentId}
          onChange={handleChange}
          className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
        >
          <option value="">Select a sub collection</option>
          {subCollections.map(collection => {
            // ✅ FIX: Handle both id and _id fields
            const collectionId = collection.id || collection._id;
            return (
              <option key={collectionId} value={collectionId}>
                {collection.name}
              </option>
            );
          })}
        </select>
        
        {selectedSubCollection && selectedParentCollection && (
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm font-medium text-blue-900">
              Will be placed under: {selectedParentCollection.name} → {selectedSubCollection.name}
            </p>
          </div>
        )}
      </div>
    )}
  </>
)}
                </div>
              </div>
            )}

            {/* Collection/Product name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                {formData.collectionType === 'standalone' ? 'Product Name *' : 'Collection Name *'}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={formData.collectionType === 'standalone' ? "Enter product name" : "Enter collection name"}
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:opacity-50"
                disabled={loading}
                required
              />
            </div>

            {/* Price and Quantity for Standalone Collections */}
            {formData.collectionType === 'standalone' && (
              <>
                <div className="mb-6 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                      Base price (₦) *
                    </label>
                    <input
                      type="number"
                      name="standaloneProductDetails.basePrice"
                      value={formData.standaloneProductDetails.basePrice}
                      onChange={handleChange}
                      placeholder="15000"
                      className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                      Stock quantity *
                    </label>
                    <input
                      type="number"
                      name="standaloneProductDetails.stockQuantity"
                      value={formData.standaloneProductDetails.stockQuantity}
                      onChange={handleChange}
                      placeholder="100"
                      className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                      required
                    />
                  </div>
                </div>

               
              </>
            )}

            {/* Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder={formData.collectionType === 'standalone' 
                  ? "Detailed product description..." 
                  : "Enter collection description..."
                }
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none disabled:opacity-50"
                disabled={loading}
                required
              />
            </div>

            {/* Cover Image */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Cover Image *
              </label>
              
              {imagePreview ? (
                <div className="mb-4">
                  <div className="relative h-48 w-full rounded-lg overflow-hidden border border-gray-200">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={removeCoverImage}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                      disabled={uploadingImage}
                    >
                      ✕
                    </button>
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-green-600">
                    {uploadingImage ? 'Uploading image...' : 'Cover image uploaded'}
                  </p>
                </div>
              ) : (
                <div className="border-2 border-dashed border-[#DBDBDB] bg-[#F5F5F5] p-8 text-center hover:border-[#D2A146] transition-colors cursor-pointer rounded disabled:opacity-50 disabled:cursor-not-allowed">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="hidden"
                    id="coverImage"
                    disabled={loading || uploadingImage}
                  />
                  <label htmlFor="coverImage" className={`cursor-pointer ${(loading || uploadingImage) ? 'opacity-50' : ''}`}>
                    <div className="flex justify-center items-center mb-4">
                      <UploadCloudIcon className="w-12 h-12 text-gray-400" />
                    </div>
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      {uploadingImage ? 'Uploading...' : 'Upload Cover Image'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formData.collectionType === 'standalone' 
                        ? 'Primary product image. Recommended: 1200 × 800px'
                        : 'Collection cover image. Recommended: 1200 × 800px'
                      }
                    </p>
                  </label>
                </div>
              )}
            </div>

            {/* Additional Images for Standalone */}
            {formData.collectionType === 'standalone' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                  Additional Images (Optional)
                </label>
                
                {/* Image Previews */}
                {additionalImagePreviews.length > 0 && (
                  <div className="mb-4">
                    <div className="grid grid-cols-3 gap-2">
                      {additionalImagePreviews.map((preview, index) => (
                        <div key={index} className="relative h-32 rounded-lg overflow-hidden border border-gray-200">
                          <img 
                            src={preview} 
                            alt={`Additional ${index + 1}`} 
                            className="h-full w-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeAdditionalImage(index)}
                            className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      {additionalImages.length} additional image(s) uploaded
                    </p>
                  </div>
                )}
                
                {/* Upload Button */}
                <div className="border-2 border-dashed border-[#DBDBDB] bg-[#F5F5F5] p-6 text-center hover:border-[#D2A146] transition-colors cursor-pointer rounded">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAdditionalImagesChange}
                    className="hidden"
                    id="additionalImages"
                    multiple
                    disabled={uploadingImage}
                  />
                  <label htmlFor="additionalImages" className={`cursor-pointer ${uploadingImage ? 'opacity-50' : ''}`}>
                    <div className="flex justify-center items-center mb-3">
                      <UploadCloudIcon className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-md font-medium text-gray-700 mb-1">
                      {uploadingImage ? 'Uploading...' : 'Upload Additional Images'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Upload multiple product images (max 9 additional images)
                    </p>
                  </label>
                </div>
              </div>
            )}

            {/* Information note */}
            <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-start gap-3">
                <CautionIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800">
                    <strong>Requirements:</strong>
                    <ul className="mt-1 list-disc list-inside">
                      <li>Base product is required for all collection types</li>
                      {formData.collectionType === 'sub' && (
                        <li>Parent collection is required for sub collections</li>
                      )}
                      {formData.collectionType === 'standalone' && (
                        <>
                          <li>Price and stock quantity are required</li>
                          <li>Cover image is required</li>
                          <li>Short highlight is required</li>
                          <li>Parent placement is optional</li>
                        </>
                      )}
                    </ul>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#DBDBDB] flex gap-3">
            <button 
              onClick={handleSaveDraft}
              disabled={loading || loadingProducts || loadingCollections || uploadingImage}
              className={`px-6 py-3 font-medium rounded transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                !loading && !loadingProducts && !loadingCollections && !uploadingImage
                  ? 'bg-[#6B635D] text-white hover:bg-[#5A534D]'
                  : 'bg-gray-300 text-gray-500'
              }`}
            >
              {loading ? 'Saving...' : 'Save as Draft'}
            </button>
            <button 
              onClick={handleCreateActive}
              disabled={loading || loadingProducts || loadingCollections || uploadingImage}
              className={`px-6 py-3 font-medium rounded transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                !loading && !loadingProducts && !loadingCollections && !uploadingImage
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500'
              }`}
            >
              {loading ? 'Creating...' : 
                formData.collectionType === 'standalone' ? 'Create Product' : 'Create Collection'
              }
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCollectionModal;